import { keysToSnakeCase } from '@hotjar/webapp/lib/convert-string';
import type { ValueOf } from 'type-fest';
import type { ProductAllowance } from '~/subscription-management/api/common/model/subscription';
import { Segment } from '~/subscription-management/external-dependencies/webapp-utils';
import type { Typegen0 } from '../../checkout/state-machine/checkout.machine.typegen';

type CheckoutEventNames =
  | 'Checkout Started v3'
  | 'Wizard Step In'
  | 'Wizard Step Out'
  | 'Price Preview event';

type WizardName = 'Checkout';

export type stateNamesToTrack = Extract<
  Typegen0['matchesStates'],
  | 'editOrder.steps.editSubscription'
  | 'editOrder.steps.invoiceDetails'
  | 'editOrder.steps.paymentDetails'
  | 'editOrder.steps.reviewOrder'
  | 'processOrder.processing'
  | 'processOrder.doneSuccess'
  | 'processOrder.doneError'
  | 'processOrder.donePending'
  | 'confirmOrder.reactivation'
  | 'confirmOrder.overdue'
>;

type PropertiesByCheckoutEventNamesMapType = {
  'Checkout Started v3': CheckoutStartedProps;
  'Wizard Step In': WizardStepInProps;
  'Wizard Step Out': WizardStepOutProps;
  'Price Preview event': PricePreviewedProps;
};

type Step = ValueOf<typeof prettifiedStepNameByStateNameMap>;

interface SharedProps {
  accountId: number;
  customerId: number | undefined;
}

interface CheckoutStartedProps extends SharedProps {
  onTrial: boolean;
  draftOrder: boolean;
  conversionType: 'FIRST_TIME' | 'AMENDMENT';
  referrer?: string;
  initialPlans: Pick<ProductAllowance, 'sku' | 'entity'>[];
  initialStep: Step;
}

interface PricePreviewedProps extends SharedProps {
  success: boolean;
  failureReason?: string;
}

interface WizardStepProps extends SharedProps {
  wizardName: WizardName;
  stepName: Step;
}

interface WizardStepInProps extends WizardStepProps {
  previousStep?: Step;
}

interface WizardStepOutProps extends WizardStepProps {
  nextStep?: Step;
}

export const prettifiedStepNameByStateNameMap: Record<stateNamesToTrack, string> = {
  'editOrder.steps.editSubscription': 'Manage Subscription',
  'editOrder.steps.invoiceDetails': 'Invoice Details',
  'editOrder.steps.paymentDetails': 'Payment Method',
  'editOrder.steps.reviewOrder': 'Review Order',
  'processOrder.processing': 'Processing order',
  'processOrder.donePending': 'Processing pending',
  'processOrder.doneSuccess': 'Processing finished',
  'processOrder.doneError': 'Processing finished with error',
  'confirmOrder.reactivation': 'Confirm cancel downgrades',
  'confirmOrder.overdue': 'Subscription overdue',
};

export const trackCheckout = <T extends CheckoutEventNames>(
  eventName: T,
  properties: PropertiesByCheckoutEventNamesMapType[T],
): void => {
  // We ignored the error here, as currently, Segment track function doesn't support having nested objects, but it works correctly on Segments side.
  // TODO: There is a discussion ongoing and we will decide what is the best thing to do after we have a decision:
  // https://hotjar.slack.com/archives/CDRS7AM5M/p1661439096252679

  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  Segment.track(eventName, keysToSnakeCase(properties, true));
};
